import { useForumType } from '@/components/hooks/useForumType';
import React, {useState, useEffect, useRef, useMemo} from 'react';
import classNames from 'classnames';
import { useCurrentUser } from '../common/withUser'
import withErrorBoundary from '../common/withErrorBoundary'
import { useDialog } from '../common/withDialog';
import { hideUnreviewedAuthorCommentsSettings, type ForumTypeString } from '@/lib/instanceSettings';
import { userCanDo } from '../../lib/vulcan-users/permissions';
import { PermissionsPostMinimumInfo, requireNewUserGuidelinesAck, userIsAllowedToComment } from '../../lib/collections/users/helpers';
import { useMessages } from '../common/withMessages';
import { afNonMemberDisplayInitialPopup, useAfNonMemberSuccessHandling } from "../../lib/alignment-forum/displayAFNonMemberPopups";
import { TagCommentType } from '../../lib/collections/comments/types';
import { commentDefaultToAlignment } from '../../lib/collections/comments/helpers';
import { isInFuture, useCurrentTime } from '../../lib/utils/timeUtil';
import moment from 'moment';
import { useTracking } from "../../lib/analyticsEvents";
import { registerComponent } from "../../lib/vulcan-lib/components";
import { getCommentsNewFormPadding } from '@/lib/collections/comments/constants';
import { CommentForm, type CommentInteractionType } from './CommentForm';
import NewUserGuidelinesDialog from "./NewUserGuidelinesDialog";
import ModerationGuidelinesBox from "./ModerationGuidelines/ModerationGuidelinesBox";
import RecaptchaWarning from "../common/RecaptchaWarning";
import NewCommentModerationWarning from "../sunshineDashboard/NewCommentModerationWarning";
import RateLimitWarning from "../editor/RateLimitWarning";
import { useQuery } from "@/lib/crud/useQuery";
import { gql } from "@/lib/generated/gql-codegen";
import { useLocation } from '@/lib/routeUtil';
import { defineStyles } from '@/components/hooks/defineStyles';
import { useStyles } from '@/components/hooks/useStyles';
import CantCommentExplanation from './CantCommentExplanation';

const UsersCurrentCommentRateLimitQuery = gql(`
  query CommentsNewForm($documentId: String, $postId: String) {
    user(input: { selector: { documentId: $documentId } }) {
      result {
        ...UsersCurrentCommentRateLimit
      }
    }
  }
`);

export type FormDisplayMode = "default" | "minimalist"


const styles = defineStyles('CommentsNewForm', (theme: ThemeType) => ({
  root: {},
  rootMinimalist: {
    '& .form-input': {
      width: "100%",
      margin: 0,
      marginTop: 4,
    },
    '& form': {
      display: "flex",
      flexDirection: "row",
    }
  },
  rootQuickTakes: {
    "& .form-component-EditorFormComponent": {
      background: theme.palette.grey[100],
      padding: getCommentsNewFormPadding(theme),
      borderTopLeftRadius: theme.borderRadius.quickTakesEntry,
      borderTopRightRadius: theme.borderRadius.quickTakesEntry,
    },
  },
  quickTakesSubmitButtonAtBottom: {},
  loadingRoot: {
    opacity: 0.5
  },
  form: {
    padding: getCommentsNewFormPadding(theme),
  },
  formMinimalist: {
    padding: '12px 10px 8px 10px',
  },
  quickTakesForm: {
    display: 'flex',
    flexDirection: 'column',
  },
  rateLimitNote: {
    paddingTop: '4px',
    color: theme.palette.text.dim2,
  },
  modNote: {
    paddingTop: '4px',
    color: theme.palette.text.dim2,
  },
  moderationGuidelinesWrapper: {
    backgroundColor: theme.palette.greyAlpha(.07),
  }
}));

export type CommentSuccessCallback = ((
  comment: CommentsList,
) => void | Promise<void>) | (() => Promise<unknown>);

export type CommentCancelCallback = (...args: unknown[]) => void | Promise<void>;

const shouldOpenNewUserGuidelinesDialog = (
  maybeProps: { user: UsersCurrent | null, post?: PostsMinimumInfo },
  forumType: ForumTypeString,
): maybeProps is { user: UsersCurrent, post: PostsMinimumInfo } => {
  const { user, post } = maybeProps;
  return !!user && requireNewUserGuidelinesAck(user, forumType) && !!post;
};

const getSubmitLabel = (isQuickTake: boolean, isAnswer?: boolean) => {
  return "Submit";
}

export type CommentsNewFormProps = {
  prefilledProps?: any,
  post?: PostsMinimumInfo & PermissionsPostMinimumInfo & { question?: boolean },
  tag?: TagBasicInfo,
  tagCommentType?: TagCommentType,
  parentComment?: CommentsList,
  successCallback?: CommentSuccessCallback,
  cancelCallback?: CommentCancelCallback,
  interactionType: CommentInteractionType,
  // RM: this no longer does anything; it's used in two places to remove the `af` field
  // but I don't think it really matters.
  removeFields?: any,
  formProps?: {
    post?: PostsDetails;
    maxHeight?: boolean;
    editorHintText?: string;
  },
  enableGuidelines?: boolean,
  padding?: boolean,
  formStyle?: FormDisplayMode,
  overrideHintText?: string,
  quickTakesSubmitButtonAtBottom?: boolean,
  isAnswer?: boolean,
  cancelLabel?: string,
  hideAlignmentForumCheckbox?: boolean,
  className?: string,
}

const CommentsNewForm = ({prefilledProps={}, post, tag, tagCommentType="DISCUSSION", parentComment, successCallback, interactionType, cancelCallback, removeFields, formProps, enableGuidelines=true, padding=true, formStyle="default", overrideHintText, quickTakesSubmitButtonAtBottom, isAnswer, cancelLabel, hideAlignmentForumCheckbox, className}: CommentsNewFormProps) => {
  const { forumType } = useForumType();
  const classes = useStyles(styles);
  const currentUser = useCurrentUser();
  const { captureEvent } = useTracking({eventProps: { postId: post?._id, tagId: tag?._id, tagCommentType}});
  const now = useCurrentTime();
  const commentSubmitStartTimeRef = useRef(now.getTime());
  
  const { refetch, data } = useQuery(UsersCurrentCommentRateLimitQuery, {
    variables: { documentId: currentUser?._id, postId: post?._id },
    skip: !currentUser,
    fetchPolicy: "cache-and-network",
    ssr: false,
  });
  const document = data?.user?.result;
  const userNextAbleToComment = document?.rateLimitNextAbleToComment;
  const lastRateLimitExpiry: Date|null = (userNextAbleToComment && new Date(userNextAbleToComment.nextEligible)) ?? null;
  const rateLimitMessage = userNextAbleToComment ? userNextAbleToComment.rateLimitMessage : null
  
  // Disable the form if there's a rate limit and it's more than 1 minute until it
  // expires. (If the user is rate limited but it will expire sooner than that,
  // don't disable the form; it'll probably expire before they finish typing their
  // comment anyways, and this avoids an awkward interaction with the 15-second
  // rate limit that's only supposed to be there to prevent accidental double posts.
  // TODO
  const formDisabledDueToRateLimit = !!lastRateLimitExpiry && isInFuture(moment(lastRateLimitExpiry).subtract(1,'minutes').toDate());

  const {flash} = useMessages();
  prefilledProps = {
    ...prefilledProps,
    af: commentDefaultToAlignment(currentUser, post, forumType, parentComment),
  };
  
  const isQuickTake = !!prefilledProps.shortform
  const isMinimalist = formStyle === "minimalist"
  const [showGuidelines, setShowGuidelines] = useState(false)
  const [loading, setLoading] = useState(false)
  const [commentFormInstance, setCommentFormInstance] = useState(0)
  const [_,setForceRefreshState] = useState(0);

  const { openDialog } = useDialog();
  
  // On focus (this bubbles out from the text editor), show moderation guidelines.
  // Defer this through a setTimeout, because otherwise clicking the Cancel button
  // doesn't work (the focus event fires before the click event, the state change
  // causes DOM nodes to get replaced, and replacing the DOM nodes prevents the
  // rest of the click event handlers from firing.)
  const onFocusCommentForm: React.FocusEventHandler = () => {
    setTimeout(() => {
      // TODO: user field for showing new user guidelines
      // TODO: decide if post should be required?  We might not have a post param in the case of shortform, not sure where else
      const dialogProps = { user: currentUser, post };
      if (shouldOpenNewUserGuidelinesDialog(dialogProps, forumType)) {
        openDialog({
          name: 'NewUserGuidelinesDialog',
          contents: ({onClose}) => <NewUserGuidelinesDialog
            onClose={onClose}
            post={dialogProps.post}
          />
        });
      }
      setShowGuidelines(true);
    }, 0);
  };

  const afNonMemberSuccessHandling = useAfNonMemberSuccessHandling();

  const { pathname } = useLocation();
  const wrappedSuccessCallback = (comment: CommentsList) => {
    afNonMemberSuccessHandling(comment);
    if (comment.deleted && comment.deletedReason) {
      flash(comment.deletedReason);
    }
    if (comment.draft && comment.shortform && pathname === '/') {
      flash("Quick take saved as draft. You can resume it from your drafts page.");
    }
    if (successCallback) {
      void successCallback(comment);
    }
    setLoading(false)
    // Remount the form after successful submission so editor/form state does
    // not persist between comments.
    setCommentFormInstance((prev) => prev + 1);
    const timeElapsed = Date.now() - commentSubmitStartTimeRef.current;
    captureEvent("wrappedSuccessCallbackFinished", {timeElapsed, commentId: comment._id})
    void refetch();
  };

  const wrappedCancelCallback = (...args: unknown[]) => {
    if (cancelCallback) {
      void cancelCallback(...args);
    }
    setLoading(false)
  };

  if (post) {
    prefilledProps = {
      ...prefilledProps,
      postId: post._id
    };
  }

  if (tag) {
    prefilledProps = {
      ...prefilledProps,
      tagId: tag._id,
      tagCommentType: tagCommentType,
    };
  }

  if (parentComment) {
    prefilledProps = {
      ...prefilledProps,
      parentCommentId: parentComment._id,
    };
  }

  prefilledProps = {
    ...prefilledProps,
    answer: !!isAnswer,
  };

  const hideDate = hideUnreviewedAuthorCommentsSettings.get(forumType);
  const commentWillBeHidden = (
    hideDate
    && new Date(hideDate) < now
    && currentUser
    && !currentUser.isReviewed
  );

  const extraFormProps = useMemo(() => ({
    ...(isMinimalist ? {commentMinimalistStyle: true, editorHintText: "Reply..."} : {}),
    ...(overrideHintText ? {editorHintText: overrideHintText} : {})
  }), [isMinimalist, overrideHintText]);

  const parentDocumentId = post?._id || tag?._id

  useEffect(() => {
    // If disabled due to rate limit, set a timer to reenable the comment form when the rate limit expires
    if (formDisabledDueToRateLimit && userNextAbleToComment) {
      const timeLeftOnUserRateLimitMS = new Date(userNextAbleToComment).getTime() - new Date().getTime()
      const timer = setTimeout(() => {
        setForceRefreshState((n) => (n+1));
      }, timeLeftOnUserRateLimitMS);
      
      return () => clearTimeout(timer);
    }
  }, [userNextAbleToComment, formDisabledDueToRateLimit]);

  const mergedFormProps = useMemo(() => ({
    formClassName: isQuickTake ? classes.quickTakesForm : '',
    ...extraFormProps,
    ...formProps,
  }), [isQuickTake, classes.quickTakesForm, extraFormProps, formProps]);

  const commentSubmitProps = useMemo(() => ({
    formDisabledDueToRateLimit,
    isQuickTake,
    quickTakesSubmitButtonAtBottom,
    loading,
  }), [formDisabledDueToRateLimit, isQuickTake, quickTakesSubmitButtonAtBottom, loading]);
  
  const permissionPost = post ?? prefilledProps;
  if (currentUser && !userCanDo(currentUser, `posts.moderate.all`) && !userIsAllowedToComment(currentUser, permissionPost, post?.user ?? null, !!parentComment)) {
    if (post) {
      return <CantCommentExplanation post={post} />
    }
    return <span>Sorry, you do not have permission to comment at this time.</span>
  }
  return (
    <div className={classNames(
      className,
      isMinimalist ? classes.rootMinimalist : classes.root,
      {
        [classes.loadingRoot]: loading,
        [classes.rootQuickTakes]: isQuickTake,
        [classes.quickTakesSubmitButtonAtBottom]: isQuickTake && quickTakesSubmitButtonAtBottom,
      }
    )} onFocus={onFocusCommentForm}>
      <RecaptchaWarning currentUser={currentUser}>
        <div className={padding
          ? classNames({
            [classes.form]: !isMinimalist && !(isQuickTake && quickTakesSubmitButtonAtBottom),
            [classes.formMinimalist]: isMinimalist,
          })
          : undefined
        }>
          {formDisabledDueToRateLimit && <RateLimitWarning
            contentType="comment"
            lastRateLimitExpiry={lastRateLimitExpiry}
            rateLimitMessage={rateLimitMessage}
          />}
          <div onFocus={(ev) => {
            afNonMemberDisplayInitialPopup(currentUser, openDialog, forumType)
            ev.preventDefault()
          }}>
            <CommentForm
              key={commentFormInstance}
              prefilledProps={prefilledProps}
              commentSubmitProps={commentSubmitProps}
              // Note: This is overly restrictive at the moment to focus on the core use case first, many of these would work
              disableSubmitDropdown={isAnswer || post?.question || prefilledProps.tagId}
              interactionType={interactionType}
              alignmentForumPost={post?.af}
              hideAlignmentForumCheckbox={hideAlignmentForumCheckbox}
              formClassName={mergedFormProps.formClassName}
              editorHintText={mergedFormProps.editorHintText}
              commentMinimalistStyle={mergedFormProps.commentMinimalistStyle}
              maxHeight={mergedFormProps.maxHeight}
              submitLabel={getSubmitLabel(isQuickTake, isAnswer)}
              cancelLabel={cancelLabel}
              onSuccess={wrappedSuccessCallback}
              onCancel={wrappedCancelCallback}
              onSubmit={() => {
                setLoading(true);
                commentSubmitStartTimeRef.current = Date.now()
                captureEvent("wrappedSubmitCallbackStarted")
              }}
              onError={() => {
                setLoading(false)
              }}
            />
          </div>
        </div>
        {parentDocumentId && enableGuidelines && showGuidelines && <div className={classes.moderationGuidelinesWrapper}>
          {commentWillBeHidden && <div className={classes.modNote}>
            <NewCommentModerationWarning />
          </div>}
          <ModerationGuidelinesBox documentId={parentDocumentId} commentType={post?._id ? "post" : "subforum"} />
        </div>}
      </RecaptchaWarning>
    </div>
  );
};

export default registerComponent('CommentsNewForm', CommentsNewForm, {
  hocs: [withErrorBoundary]
});



